import { MAX_PLAYERS } from "./constants.ts";

/**
 * Teams (GDD §6.2: 3v3 / 4v4). Two teams; `None` is the free-for-all. The
 * values travel on the wire (`PlayerState.team`).
 */
export const Team = {
  None: 0,
  Blue: 1,
  Red: 2,
} as const;
export type TeamId = (typeof Team)[keyof typeof Team];

export const TEAMS = [Team.Blue, Team.Red] as const;

/** Spawn indices from this value up are team spawns (see `spawnIndexForTeam`). */
export const TEAM_SPAWN_BASE = 16;
const TEAM_SPAWN_SLOTS = MAX_PLAYERS;

export interface TeamMember {
  readonly id: string;
  /** `Team`; `Team.None` while not assigned. */
  readonly team: number;
  /** Join order: the last to join moves first when rebalancing. */
  readonly order: number;
}

export function isTeam(value: unknown): value is TeamId {
  return value === Team.Blue || value === Team.Red;
}

/** Whether two fighters are on the same team: allies never hit each other (D27). */
export function areAllies(a: number, b: number): boolean {
  return a !== Team.None && a === b;
}

export function teamSizes(members: readonly TeamMember[]): [number, number] {
  let blue = 0;
  let red = 0;
  for (const m of members) {
    if (m.team === Team.Blue) blue += 1;
    else if (m.team === Team.Red) red += 1;
  }
  return [blue, red];
}

/** The smaller team, Blue on a tie. */
export function smallerTeam(members: readonly TeamMember[]): TeamId {
  const [blue, red] = teamSizes(members);
  return red < blue ? Team.Red : Team.Blue;
}

/** A player may change team as long as the teams stay within one player of each other (D29). */
export function canSwitchTeam(members: readonly TeamMember[], id: string, to: number): boolean {
  const me = members.find((m) => m.id === id);
  if (!me || !isTeam(to) || me.team === to) return false;
  const moved = members.map((m) => (m.id === id ? { ...m, team: to } : m));
  const [blue, red] = teamSizes(moved);
  return Math.abs(blue - red) <= 1;
}

/**
 * Teams fixed up so they differ by at most one player: unassigned players
 * join the smaller team, then the latest to join leave the larger one.
 * Everyone else keeps their team. Returns id → team.
 */
export function balanceTeams(members: readonly TeamMember[]): Map<string, TeamId> {
  const result = new Map<string, TeamId>();
  const byOrder = [...members].sort((a, b) => a.order - b.order);
  for (const m of byOrder) if (isTeam(m.team)) result.set(m.id, m.team);
  const current = (): TeamMember[] => byOrder.filter((m) => result.has(m.id)).map((m) => ({ ...m, team: result.get(m.id)! }));
  for (const m of byOrder) if (!result.has(m.id)) result.set(m.id, smallerTeam(current()));

  for (;;) {
    const [blue, red] = teamSizes(current());
    if (Math.abs(blue - red) <= 1) return result;
    const from = blue > red ? Team.Blue : Team.Red;
    const latest = [...byOrder].reverse().find((m) => result.get(m.id) === from)!;
    result.set(latest.id, from === Team.Blue ? Team.Red : Team.Blue);
  }
}

/** Random teams of equal size (± 1). `randomInt(n)` is uniform in [0, n). */
export function shuffleTeams(members: readonly TeamMember[], randomInt: (max: number) => number): Map<string, TeamId> {
  const ids = members.map((m) => m.id);
  for (let i = ids.length - 1; i > 0; i--) {
    const j = randomInt(i + 1);
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
  }
  // Random first team too, so the odd player out is not always Blue.
  const first = randomInt(2) === 0 ? Team.Blue : Team.Red;
  const second = first === Team.Blue ? Team.Red : Team.Blue;
  return new Map(ids.map((id, i) => [id, i % 2 === 0 ? first : second]));
}

/** Team score that wins a match with these teams: `perPlayer` per player of the larger team (D28). */
export function teamGoal(members: readonly TeamMember[], perPlayer: number): number {
  const [blue, red] = teamSizes(members);
  return perPlayer * Math.max(1, blue, red);
}

/** Winning team from the two team scores; `Team.None` for a draw. */
export function teamWinner(blue: number, red: number): number {
  if (blue === red) return Team.None;
  return blue > red ? Team.Blue : Team.Red;
}

/**
 * Spawn index of the `slot`-th player of a team. It is stored in the fighter
 * simulation like any other spawn index, so a respawn after a fall (which
 * the client predicts) lands on the team side as well.
 */
export function spawnIndexForTeam(team: TeamId, slot: number): number {
  return TEAM_SPAWN_BASE + (team - 1) * TEAM_SPAWN_SLOTS + (slot % TEAM_SPAWN_SLOTS);
}

/** Inverse of `spawnIndexForTeam`; null for a free-for-all spawn index. */
export function teamSpawnSlot(index: number): { team: TeamId; slot: number } | null {
  if (index < TEAM_SPAWN_BASE) return null;
  const offset = index - TEAM_SPAWN_BASE;
  const team = Math.floor(offset / TEAM_SPAWN_SLOTS) + 1;
  if (!isTeam(team)) return null;
  return { team, slot: offset % TEAM_SPAWN_SLOTS };
}
